'use client';

import { SidebarMenuItem, SidebarMenuButton, useSidebar } from '@ui/components/ui/sidebar';
import { Bell, MoreHorizontal, Trash2, CheckCheck } from 'lucide-react';
import { useTranslations } from 'next-intl';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/components/ui/dropdown-menu';
import { Button } from '@ui/components/ui/button';
import { ScrollArea } from '@ui/components/ui/scroll-area';
import { cn } from '@ui/lib/utils';
import { useDateFormatting } from '@/hooks/use-date-formatting';
import { useNotifications } from '@/hooks/use-notifications';
import { useFaviconNotification } from '@/hooks/use-favicon-notification';
import { useTransition, useEffect, useRef, useState } from 'react';
import { toast } from '@ui/components/ui/sonner';
import LinkWrapper from '@/lib/tools/link-wrapper';

export default function NavNotifications() {
  const t = useTranslations('Navigation');
  const tNotifications = useTranslations('Notifications');
  const { timeAgo } = useDateFormatting();
  const { isMobile, open } = useSidebar();
  const { notifications, setNotifications, markAsRead, markAllAsRead, deleteAllNotifications } = useNotifications();
  const [isPending, startTransition] = useTransition();
  const [isOpen, setIsOpen] = useState(false);
  const autoReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const unreadNotifications = notifications ? notifications.filter((n) => !n.isRead) : [];
  const hasNewNotifications = unreadNotifications.length > 0;

  // Use the favicon notification hook to change favicon when there are unread notifications
  useFaviconNotification(hasNewNotifications);

  // Handle auto-read after 3 seconds when dropdown is open
  useEffect(() => {
    // Clear any existing timer
    if (autoReadTimerRef.current) {
      clearTimeout(autoReadTimerRef.current);
      autoReadTimerRef.current = null;
    }

    // Only start timer if dropdown is open and there are unread notifications
    if (isOpen && unreadNotifications.length > 0) {
      autoReadTimerRef.current = setTimeout(() => {
        startTransition(async () => {
          try {
            // Mark all unread notifications as read
            await markAllAsRead();
            // Update local state to reflect the changes
            setNotifications((prev) => prev?.map((n) => ({ ...n, isRead: true })));
          } catch (error) {
            // Silently fail - user can still manually mark as read if needed
            console.error('Failed to auto-mark notifications as read:', error);
          }
        });
      }, 3000); // 3 second delay
    }

    // Cleanup timer on unmount or when dependencies change
    return () => {
      if (autoReadTimerRef.current) {
        clearTimeout(autoReadTimerRef.current);
        autoReadTimerRef.current = null;
      }
    };
  }, [isOpen, unreadNotifications.length, startTransition, markAllAsRead, setNotifications]);

  return (
    <SidebarMenuItem>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            tooltip={t('notifications')}
            isActive={false}
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground relative"
          >
            <Bell />
            {hasNewNotifications && !open && (
              <span className="absolute right-1 top-1 flex h-2 w-2">
                <span className="relative inline-flex h-full w-full rounded-full bg-destructive"></span>
              </span>
            )}
            <span>{t('notifications')}</span>
            {hasNewNotifications && open && (
              <span className="ml-auto inline-flex size-5 items-center justify-center rounded-full bg-destructive p-2 text-xs font-semibold text-white">
                {unreadNotifications.length}
              </span>
            )}
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-[--radix-dropdown-menu-trigger-width] min-w-80 rounded-lg max-w-[95vw]"
          side={isMobile ? 'bottom' : 'right'}
          sideOffset={4}
        >
          <DropdownMenuLabel className="flex items-center justify-between p-2 font-normal">
            <div className="font-semibold">{tNotifications('inbox')}</div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="size-7">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="min-w-40 rounded-lg" side="bottom" align="end">
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      startTransition(async () => {
                        markAllAsRead()
                          .then(() => setNotifications((prev) => prev?.map((n) => ({ ...n, isRead: true }))))
                          .catch(() => toast.error(tNotifications('markAllAsReadError')));
                      });
                    }}
                    disabled={isPending}
                  >
                    <CheckCheck className="mr-2 size-4" />
                    <span>{tNotifications('markAllAsRead')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      startTransition(async () => {
                        deleteAllNotifications()
                          .then(() => setNotifications([]))
                          .catch(() => toast.error(tNotifications('deleteAllError')));
                      });
                    }}
                    disabled={isPending}
                  >
                    <Trash2 className="mr-2 size-4" />
                    <span>{tNotifications('deleteAll')}</span>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {!notifications ? (
            <LoadingNotifications />
          ) : notifications.length > 0 ? (
            <ScrollArea className="h-96">
              <div className="flex flex-col">
                {notifications.map((notification) => (
                  <LinkWrapper
                    key={notification.id}
                    href={notification.url}
                    className={cn('flex items-start gap-3 p-3 hover:bg-muted/50', !notification.isRead && 'bg-muted')}
                    onClick={async () => {
                      if (!notification.isRead)
                        markAsRead(notification.id)
                          .then(() => setNotifications((prev) => prev?.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n))))
                          .catch(() => toast.error(tNotifications('markAsReadError')));
                    }}
                  >
                    {!notification.isRead && <div className="mt-1 size-2 rounded-full bg-primary" />}
                    <div className="flex-1 space-y-1">
                      <p className="text-sm">{notification.message}</p>
                      <p className="text-xs text-muted-foreground">{timeAgo(notification.createdAt)}</p>
                    </div>
                  </LinkWrapper>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
              <div className="rounded-full bg-muted p-3">
                <Bell className="size-8 text-muted-foreground" />
              </div>
              <div className="space-y-1">
                <p className="font-semibold">{tNotifications('noUnread')}</p>
                <p className="text-sm text-muted-foreground">{tNotifications('weWillLetYouKnow')}</p>
              </div>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

const LoadingNotifications = () => (
  <div className="animate-pulse flex items-start gap-3 p-3">
    <div className="mt-1 size-2 rounded-full bg-muted" />
    <div className="flex-1 space-y-2">
      <div className="h-4 w-3/4 rounded bg-muted" />
      <div className="h-3 w-1/2 rounded bg-muted" />
    </div>
  </div>
);
